import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Cookie, Mail, FileText, Database, Lock, MapPin, Globe2, Users, Clock, Scale,
} from "lucide-react";
import { openCookieSettings } from "@/lib/consent";

const Section = ({ icon: Icon, title, children, testId }) => (
  <section className="scroll-mt-32" data-testid={testId}>
    <h2 className="font-heading text-xl font-bold text-[#1A2433] mb-4 flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-[#991B1B]" />}
      {title}
    </h2>
    <div className="text-slate-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

const Sub = ({ name, role, country }) => (
  <li>
    <strong>{name}</strong> – {role} {country && <span className="text-slate-500">({country})</span>}
  </li>
);

export default function DatenschutzPage() {
  return (
    <div className="pt-32 pb-24 bg-slate-50 min-h-screen" data-testid="datenschutz-page">
      <div className="mbw-container max-w-4xl">
        <div className="mbw-overline mb-4">Rechtliches</div>
        <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tighter text-[#1A2433] leading-[1.02] mb-4">
          Datenschutzerklärung
        </h1>
        <p className="text-lg text-slate-600 mb-2">
          Wir nehmen den Schutz Ihrer persönlichen Daten ernst und behandeln Ihre personenbezogenen
          Daten vertraulich gemäss dem revidierten schweizerischen Datenschutzgesetz (revDSG) sowie –
          soweit anwendbar – der EU-Datenschutz-Grundverordnung (DSGVO).
        </p>
        <p className="text-sm text-slate-500 mb-10">
          <strong>Version 1.0 · Stand: Februar 2026</strong>
        </p>

        <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 space-y-10">
          {/* 1. Verantwortlicher */}
          <Section icon={ShieldCheck} title="1. Verantwortliche Stelle" testId="dse-verantwortlich">
            <div>
              <div className="font-semibold text-[#1A2433]">MBW Swiss AG</div>
              <div>Wildenstrasse 3, CH-8212 Neuhausen am Rheinfall</div>
              <div>Schweiz</div>
              <div className="mt-2">
                Vertretungsberechtigt: <strong>Matthias Kirchner</strong>, Verwaltungsrat
              </div>
              <div>UID / HR: CHE-107.981.530 (Kanton Schaffhausen)</div>
              <div className="mt-2">E-Mail: <a href="mailto:office@mbw-swiss.ch" className="text-[#991B1B] hover:underline">office@mbw-swiss.ch</a></div>
              <div>Telefon: +41 52 551 01 40</div>
            </div>
            <p className="text-sm text-slate-500 pt-2 border-t border-slate-100 mt-3">
              <strong>Datenschutzanfragen</strong> richten Sie bitte ebenfalls an die oben genannte
              Kontaktadresse. Eine separate Datenschutzbeauftragte / ein separater Datenschutz­beauftragter
              ist nicht bestellt.
            </p>
          </Section>

          {/* 2. Anwendungsbereich */}
          <Section icon={Globe2} title="2. Anwendungsbereich (Schweiz / EU)" testId="dse-scope">
            <p>
              Diese Datenschutzerklärung gilt für die Bearbeitung personenbezogener Daten durch die
              MBW Swiss AG im Zusammenhang mit dieser Website (mbw-swiss.ch) sowie unseren Treuhand-,
              Steuer- und Gründungsdienstleistungen.
            </p>
            <p>
              Soweit Sie Ihren Wohnsitz in der EU haben und unsere Dienstleistungen in Anspruch nehmen,
              kann zusätzlich die EU-DSGVO Anwendung finden. Da unsere Tätigkeit gegenüber Personen aus
              der EU nur <em>gelegentlich</em> erfolgt und nicht systematisch auf den EU-Markt
              ausgerichtet ist, haben wir derzeit <strong>keinen EU-Vertreter nach Art. 27 DSGVO</strong>
              bestellt. Anfragen aus dem EU-Raum können direkt an die oben genannte Schweizer Kontaktadresse
              gerichtet werden.
            </p>
          </Section>

          {/* 3. Welche Daten */}
          <Section icon={Database} title="3. Welche Daten wir bearbeiten" testId="dse-daten">
            <p>Wir bearbeiten ausschliesslich Daten, die Sie uns aktiv übermitteln:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Kontaktformular</strong>: Name, E-Mail, Telefon, Betreff, Nachricht.</li>
              <li><strong>Kundenportal (Login)</strong>: Vorname, Nachname, E-Mail, Firma, Telefon, Passwort-Hash.</li>
              <li><strong>Intake- / Onboarding-Formular</strong>: Angaben zu Person, Vorhaben, Gesellschaft, Branche, KYC-Daten (wirtschaftlich Berechtigte, PEP-Status, Mittelherkunft, Ausweisdokumente).</li>
              <li><strong>Kostenrechner</strong>: gewählte Leistungen, Add-ons, Eingaben (nur gespeichert, wenn Sie eingeloggt sind).</li>
              <li><strong>KI-Assistent (Chat)</strong>: Inhalt der eingegebenen Nachrichten zur Beantwortung der Anfrage.</li>
              <li><strong>Generierte Vertragsdokumente</strong>: PDF-Versionen Ihrer Beratungs-, Gründungs- und Vollmachts­unterlagen.</li>
              <li><strong>Server-Logs</strong>: IP-Adresse, User-Agent, Zeitstempel der Zugriffe – ausschliesslich zur Sicherstellung des Betriebs und der IT-Sicherheit.</li>
            </ul>
          </Section>

          {/* 4. Zwecke und Rechtsgrundlagen */}
          <Section icon={FileText} title="4. Zwecke &amp; Rechtsgrundlagen" testId="dse-zwecke">
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Mandatsanbahnung &amp; -durchführung</strong> (Art. 31 Abs. 1 revDSG / Art. 6 Abs. 1 lit. b DSGVO – Vertragserfüllung).</li>
              <li><strong>Erfüllung gesetzlicher Pflichten</strong> (Buchführung, Geldwäschereigesetz, KYC) – Art. 31 Abs. 2 lit. a revDSG / Art. 6 Abs. 1 lit. c DSGVO.</li>
              <li><strong>Sicherheit und Betrieb der Website</strong> (berechtigtes Interesse) – Art. 31 Abs. 2 lit. b revDSG / Art. 6 Abs. 1 lit. f DSGVO.</li>
              <li><strong>Beantwortung von Anfragen</strong> über Chat-Assistent und Kontaktformular – Art. 6 Abs. 1 lit. b / f DSGVO.</li>
            </ul>
          </Section>

          {/* 5. Cookies und Tracking */}
          <Section icon={Cookie} title="5. Cookies, Local Storage &amp; Tracking" testId="dse-cookies">
            <p>
              Wir setzen folgende Kategorien von Cookies / Local-Storage-Einträgen ein. Ihre Einwilligung
              können Sie jederzeit über den{" "}
              <button
                type="button"
                onClick={() => openCookieSettings()}
                className="text-[#991B1B] underline hover:no-underline font-medium"
                data-testid="dse-open-cookie-settings"
              >
                Cookie-Manager
              </button>{" "}
              anpassen oder widerrufen.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Notwendig (immer aktiv):</strong> Login-Session des Kundenportals
                (HTTP-only Cookie), CSRF-Schutz, Einwilligungsstatus des Cookie-Banners
                (<code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">mbw_cookie_consent_v1</code> im
                Local Storage). Rechtsgrundlage: Vertragsdurchführung / berechtigtes Interesse.
              </li>
              <li>
                <strong>Funktional (optional):</strong> Speicherung von Kalkulator-Eingaben,
                Komfortfunktionen. Nur aktiv mit Ihrer Einwilligung.
              </li>
              <li>
                <strong>Statistik (optional):</strong> Aktuell <strong>nicht im Einsatz</strong>.
                Sobald wir anonymisierte Reichweitenstatistiken einführen (z. B. Plausible oder
                Matomo), wird dies hier ergänzt und ist nur mit Ihrer Einwilligung aktiv.
              </li>
              <li>
                <strong>Marketing (optional):</strong> Aktuell <strong>nicht im Einsatz</strong>.
                Werbe- oder Retargeting-Tools werden nur nach ausdrücklicher Einwilligung gesetzt.
              </li>
            </ul>
          </Section>

          {/* 6. Eingebettete Inhalte */}
          <Section icon={MapPin} title="6. Eingebettete Karten (OpenStreetMap)" testId="dse-osm">
            <p>
              Auf unserer Kontaktseite betten wir eine Karte der{" "}
              <strong>OpenStreetMap Foundation</strong> (St John's Innovation Centre, Cowley Road,
              Cambridge, CB4 0WS, Vereinigtes Königreich) ein. Beim Aufruf der Karte wird Ihre
              IP-Adresse an OpenStreetMap übertragen. OpenStreetMap setzt selbst keine Tracking-Cookies.
              Datenschutzhinweise:{" "}
              <a href="https://wiki.osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener noreferrer" className="text-[#991B1B] hover:underline">
                osmfoundation.org/wiki/Privacy_Policy
              </a>.
            </p>
            <p className="text-sm text-slate-500">
              Vereinigtes Königreich: Angemessenheitsbeschluss durch die Schweiz und die EU vorhanden.
            </p>
          </Section>

          {/* 7. Auftragsbearbeiter & Empfänger */}
          <Section icon={Users} title="7. Auftragsbearbeiter &amp; Empfänger" testId="dse-empfaenger">
            <p>Wir setzen sorgfältig ausgewählte Dienstleister ein:</p>
            <ul className="list-disc pl-6 space-y-1">
              <Sub name="Contabo GmbH"      role="Server-Hosting der Website &amp; des Portals"     country="Deutschland" />
              <Sub name="Hostpoint AG"      role="E-Mail-Versand (SMTP) für System- und Offerten-Mails" country="Schweiz" />
              <Sub name="OpenStreetMap Foundation" role="Eingebettete Karte auf der Kontaktseite"  country="UK" />
              <Sub name="ABACUS Research AG / AbaNinja" role="Buchhaltungs- und Offertenerstellung (Mandanten)" country="Schweiz" />
              <Sub name="Emergent / KI-Anbieter" role="KI-Chat-Assistent (Anthropic, OpenAI, Google)" country="USA" />
            </ul>
            <p>Im Rahmen unserer treuhänderischen Tätigkeit können Daten zudem weitergegeben werden an:</p>
            <ul className="list-disc pl-6 space-y-1">
              <Sub name="Externe Notariate"            role="Beurkundung von Gründungen und Statutenänderungen" country="Schweiz" />
              <Sub name="Externe Buchhalter:innen"     role="Unterstützung bei Mandantenbuchhaltung" country="Schweiz" />
              <Sub name="Übersetzungsagenturen"        role="Übersetzung von Dokumenten DE / EN / FR / IT" country="Schweiz / EU" />
              <Sub name="Versicherungsmakler"          role="Vermittlung von Berufshaft- und Sachversicherungen" country="Schweiz" />
              <Sub name="Banken &amp; Finanzdienstleister" role="Kontoeröffnung, Zahlungsverkehr"           country="Schweiz" />
              <Sub name="Behörden"                     role="Handelsregister, Steuerverwaltung, FINMA (gesetzliche Pflicht)" country="Schweiz" />
            </ul>
            <p className="text-sm text-slate-500">
              Mit allen Auftragsbearbeitern bestehen vertragliche Vereinbarungen, die den Schutz
              Ihrer Daten gewährleisten.
            </p>
          </Section>

          {/* 8. Drittlandübermittlung */}
          <Section icon={Globe2} title="8. Übermittlung ins Ausland (USA)" testId="dse-drittland">
            <p>
              Beim Einsatz unseres KI-Chat-Assistenten werden Ihre Eingaben zur Beantwortung an
              Anbieter mit Sitz in den USA (z. B. Anthropic, OpenAI, Google) übermittelt. Es liegt
              ein angemessenes Schutzniveau auf Basis des EU-US Data Privacy Framework bzw.
              entsprechender Standardvertragsklauseln vor. <strong>Geben Sie im Chat keine sensiblen
              oder personenbezogenen Daten Dritter ein.</strong>
            </p>
          </Section>

          {/* 9. Aufbewahrungsdauer */}
          <Section icon={Clock} title="9. Aufbewahrungsdauer" testId="dse-aufbewahrung">
            <p>Wir bewahren personenbezogene Daten nur so lange, wie es erforderlich ist:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>10 Jahre</strong> – Geschäfts-, Buchhaltungs- und Vertragsunterlagen (Art. 958f OR).</li>
              <li><strong>10 Jahre</strong> – KYC-Dokumente und Mandatsakten (Geldwäschereigesetz).</li>
              <li><strong>24 Monate</strong> – Anfragen über Kontaktformular ohne anschliessendes Mandat.</li>
              <li><strong>12 Monate</strong> – Server-Logfiles, danach automatische Löschung / Anonymisierung.</li>
              <li><strong>Bis Widerruf</strong> – Einwilligungen, Cookie-Präferenzen, Newsletter-Anmeldungen (sofern künftig eingeführt).</li>
            </ul>
          </Section>

          {/* 10. Ihre Rechte */}
          <Section icon={Scale} title="10. Ihre Rechte" testId="dse-rechte">
            <p>Sie haben gegenüber der MBW Swiss AG jederzeit das Recht auf:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Auskunft</strong> über die zu Ihrer Person bearbeiteten Daten</li>
              <li><strong>Berichtigung</strong> unrichtiger Daten</li>
              <li><strong>Löschung</strong> Ihrer Daten (soweit keine gesetzliche Aufbewahrungspflicht entgegensteht)</li>
              <li><strong>Einschränkung</strong> der Bearbeitung</li>
              <li><strong>Datenportabilität</strong> (Erhalt in maschinenlesbarem Format)</li>
              <li><strong>Widerspruch</strong> gegen die Bearbeitung</li>
              <li><strong>Widerruf erteilter Einwilligungen</strong> (z. B. Cookie-Manager) mit Wirkung für die Zukunft</li>
            </ul>
            <p>
              Bitte richten Sie Ihre Anfrage formlos per E-Mail an{" "}
              <a href="mailto:office@mbw-swiss.ch" className="text-[#991B1B] hover:underline">office@mbw-swiss.ch</a>.
              Wir können einen Identitätsnachweis verlangen, um Missbrauch auszuschliessen.
            </p>
          </Section>

          {/* 11. Beschwerderecht */}
          <Section icon={Mail} title="11. Beschwerderecht" testId="dse-beschwerde">
            <p>
              Bei Verstössen gegen das Datenschutzgesetz haben Sie das Recht, sich beim
              <strong> Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten (EDÖB)</strong>{" "}
              zu beschweren:{" "}
              <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer" className="text-[#991B1B] hover:underline">
                www.edoeb.admin.ch
              </a>.
            </p>
            <p>
              Personen mit Wohnsitz in der EU können sich zusätzlich an die für sie zuständige
              Datenschutzaufsichtsbehörde wenden.
            </p>
          </Section>

          {/* 12. Datensicherheit */}
          <Section icon={Lock} title="12. Datensicherheit" testId="dse-sicherheit">
            <p>
              Sämtliche Datenübertragungen auf dieser Website erfolgen verschlüsselt via HTTPS (TLS).
              Passwörter werden niemals im Klartext gespeichert, sondern als bcrypt-Hash. Der Zugriff
              auf personenbezogene Daten ist intern auf einen engen Kreis berechtigter Personen
              beschränkt. Hochgeladene Dokumente werden in einem zugriffsbeschränkten Bereich
              gespeichert und ausschliesslich für die Mandatserfüllung verwendet.
            </p>
          </Section>

          {/* 13. Änderungen */}
          <Section icon={FileText} title="13. Änderungen dieser Erklärung" testId="dse-aenderungen">
            <p>
              Wir behalten uns vor, diese Datenschutzerklärung anzupassen, um sie an geänderte
              Rechtslagen oder bei Änderungen unserer Dienste anzupassen. Die jeweils aktuelle Version
              finden Sie auf dieser Seite. Wesentliche Änderungen kommunizieren wir unseren Mandanten
              zusätzlich per E-Mail oder über das Kundenportal.
            </p>
            <p className="text-sm text-slate-500 pt-3 border-t border-slate-100 mt-3">
              Verweise: <Link to="/impressum" className="text-[#991B1B] hover:underline">Impressum</Link> ·{" "}
              <Link to="/impressum#agb" className="text-[#991B1B] hover:underline">AGB</Link>
            </p>
          </Section>

          <p className="text-xs text-slate-400 pt-6 border-t border-slate-200">
            MBW Swiss AG · Wildenstrasse 3 · 8212 Neuhausen am Rheinfall ·
            Version 1.0 · Stand: Februar 2026
          </p>
        </div>
      </div>
    </div>
  );
}
